import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Grid, Column, Tile, Tag, Search, Modal, InlineNotification, Pagination,
  Dropdown, TextInput, RadioButtonGroup, RadioButton, NumberInput, Select, SelectItem,
  ProgressIndicator, ProgressStep, Tooltip, DatePicker, DatePickerInput,
} from '@carbon/react';
import { Add, View, Edit, TrashCan, Information, Camera, List, Grid as GridIcon, QrCode, Renew } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';
import { useShipments } from '../hooks/useShipments';
import { useZones } from '../hooks/useZones';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { supabase } from '../services/supabaseClient';
import { createShipment, updateShipment, updateShipmentMetadata, deleteShipment, getShipmentByEpc, checkHandoffAuthorization, checkRecipientRegistered, designateRecipient, getPendingAuthorization, listActiveAggregations } from '../services/shipmentService';
import QRCodeScanner from '../components/QRCodeScanner';
import { applyGtinInputNormalization, applySsccInputNormalization, normalizeGtin14, normalizeSscc18, generateGs1ShipmentFields } from '../utils/gs1Utils';

const STATUS_TAG_TYPE = {
  commissioned: 'blue',
  in_transit: 'purple',
  delivered: 'green',
  quarantined: 'red',
};

const STATUS_LABEL = {
  commissioned: 'Commissioned',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  quarantined: 'Quarantined',
};

const FieldInfo = ({ label, info, example }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
    {label}
    <Tooltip align="bottom" label={
      <div style={{ maxWidth: '280px', padding: '4px' }}>
        {info}
        {example && <><br /><br /><em>Example: {example}</em></>}
      </div>
    }>
      <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'inline-flex', alignItems: 'center' }} aria-label={`${label} information`}>
        <Information size={14} />
      </button>
    </Tooltip>
  </span>
);

const generateTrackingNumber = () => {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const rand = crypto.randomUUID().slice(0, 8).toUpperCase();
  return `CV-${date}-${rand}`;
};

/** ISO YYYY-MM-DD → Carbon DatePicker value (m/d/Y). */
const isoDateToCarbonValue = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
};

/** Carbon DatePicker onChange dates → ISO YYYY-MM-DD (local calendar date). */
const carbonDatesToIso = (dates) => {
  const d = dates?.[0];
  if (!d || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const ShipmentsListPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDark = theme === 'dark';
  const { hasActiveSubscription } = useSubscription();
  const { shipments, isLoading, error, refresh } = useShipments();
  const { zones } = useZones();
  const { sensors } = useSensorsOptimized();

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedSensorId, setSelectedSensorId] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [epcInput, setEpcInput] = useState('');
  const [initialState, setInitialState] = useState('commissioned');
  const [isCreating, setIsCreating] = useState(false);
  const [notification, setNotification] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Wizard step state (0 = Setup, 1 = GS1 Barcode Data, 2 = Sender/Receiver/Carrier)
  const [createStep, setCreateStep] = useState(0);

  // Sender (ship-from) details
  const [senderName, setSenderName] = useState('');
  const [senderCompany, setSenderCompany] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [senderCity, setSenderCity] = useState('');
  const [senderState, setSenderState] = useState('');
  const [senderPostalCode, setSenderPostalCode] = useState('');
  const [senderCountry, setSenderCountry] = useState('');

  // Receiver (ship-to) details
  const [receiverName, setReceiverName] = useState('');
  const [receiverCompany, setReceiverCompany] = useState('');
  const [receiverAddress, setReceiverAddress] = useState('');
  const [receiverCity, setReceiverCity] = useState('');
  const [receiverState, setReceiverState] = useState('');
  const [receiverPostalCode, setReceiverPostalCode] = useState('');
  const [receiverCountry, setReceiverCountry] = useState('');

  // Carrier details
  const [carrierName, setCarrierName] = useState('');
  const [carrierTrackingNumber, setCarrierTrackingNumber] = useState('');

  // Product metadata
  const [productName, setProductName] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [gtin, setGtin] = useState('');
  const [sscc, setSscc] = useState('');
  const [quantity, setQuantity] = useState('');
  const [quantityUnit, setQuantityUnit] = useState('units');
  const [expiryDate, setExpiryDate] = useState('');

  // Authorized recipient — who is allowed to pick up / receive this shipment next.
  // Enforced server-side via shipment_authorizations + confirm_shipment_handoff RPC.
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientRole, setRecipientRole] = useState('carrier');
  const [recipientRegistered, setRecipientRegistered] = useState(null); // null | true | false
  const [recipientCheckLoading, setRecipientCheckLoading] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [shipmentToDelete, setShipmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit metadata modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editShipment, setEditShipment] = useState(null);
  const [editEpcUri, setEditEpcUri] = useState('');
  const [editSensorId, setEditSensorId] = useState(null);
  const [editZoneId, setEditZoneId] = useState(null);
  const [editStatus, setEditStatus] = useState('commissioned');
  const [editScannerOpen, setEditScannerOpen] = useState(false);
  const [editProductName, setEditProductName] = useState('');
  const [editLotNumber, setEditLotNumber] = useState('');
  const [editGtin, setEditGtin] = useState('');
  const [editSscc, setEditSscc] = useState('');
  const [editQuantity, setEditQuantity] = useState('');
  const [editQuantityUnit, setEditQuantityUnit] = useState('units');
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [isMetaSaving, setIsMetaSaving] = useState(false);

  // Edit wizard step state
  const [editStep, setEditStep] = useState(0);

  // Edit sender (ship-from) details
  const [editSenderName, setEditSenderName] = useState('');
  const [editSenderCompany, setEditSenderCompany] = useState('');
  const [editSenderAddress, setEditSenderAddress] = useState('');
  const [editSenderCity, setEditSenderCity] = useState('');
  const [editSenderState, setEditSenderState] = useState('');
  const [editSenderPostalCode, setEditSenderPostalCode] = useState('');
  const [editSenderCountry, setEditSenderCountry] = useState('');

  // Edit receiver (ship-to) details
  const [editReceiverName, setEditReceiverName] = useState('');
  const [editReceiverCompany, setEditReceiverCompany] = useState('');
  const [editReceiverAddress, setEditReceiverAddress] = useState('');
  const [editReceiverCity, setEditReceiverCity] = useState('');
  const [editReceiverState, setEditReceiverState] = useState('');
  const [editReceiverPostalCode, setEditReceiverPostalCode] = useState('');
  const [editReceiverCountry, setEditReceiverCountry] = useState('');

  // Edit carrier details
  const [editCarrierName, setEditCarrierName] = useState('');
  const [editCarrierTrackingNumber, setEditCarrierTrackingNumber] = useState('');
  const [editRecipientEmail, setEditRecipientEmail] = useState('');
  const [editRecipientRole, setEditRecipientRole] = useState('carrier');
  const [editRecipientRegistered, setEditRecipientRegistered] = useState(null);
  const [editRecipientCheckLoading, setEditRecipientCheckLoading] = useState(false);
  const [createGtinCorrected, setCreateGtinCorrected] = useState(false);
  const [createSsccCorrected, setCreateSsccCorrected] = useState(false);
  /** Step 1 GS1 fields: autogenerate (default) or manual entry */
  const [gs1InputMode, setGs1InputMode] = useState('autogenerate');
  const [editGs1InputMode, setEditGs1InputMode] = useState('manual');
  const [editGtinCorrected, setEditGtinCorrected] = useState(false);
  const [editSsccCorrected, setEditSsccCorrected] = useState(false);

  // Receive-by-scan state
  const [receiveScannerOpen, setReceiveScannerOpen] = useState(false);
  const [receiveLookingUp, setReceiveLookingUp] = useState(false);
  const [activeAggregations, setActiveAggregations] = useState([]);

  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const zoneById = useMemo(() => {
    const map = {};
    (zones || []).forEach((z) => { map[z.id] = z; });
    return map;
  }, [zones]);

  const sensorById = useMemo(() => {
    const map = {};
    (sensors || []).forEach((s) => { map[s.id] = s; });
    return map;
  }, [sensors]);

  const filteredShipments = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return shipments;
    return shipments.filter((s) => {
      const zoneName = zoneById[s.zone_id]?.name || '';
      const sensorName = sensorById[s.sensor_id]?.name || '';
      const productName = s.metadata?.product_name || '';
      return (
        (s.epc_uri || '').toLowerCase().includes(q) ||
        zoneName.toLowerCase().includes(q) ||
        sensorName.toLowerCase().includes(q) ||
        productName.toLowerCase().includes(q)
      );
    });
  }, [shipments, searchTerm, zoneById, sensorById]);

  // Pagination calculations (only for table view)
  const totalItems = filteredShipments.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedShipments = viewMode === 'table'
    ? filteredShipments.slice(startIndex, endIndex)
    : filteredShipments;

  // Reset to page 1 when search or view mode changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, viewMode]);

  useEffect(() => {
    if (!hasActiveSubscription) {
      setActiveAggregations([]);
      return undefined;
    }
    const loadAggregations = () => {
      listActiveAggregations()
        .then((rows) => setActiveAggregations(rows || []))
        .catch(() => setActiveAggregations([]));
    };
    loadAggregations();

    if (!user?.id) return undefined;

    const channel = supabase
      .channel(`shipment-aggregations-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shipment_aggregations' },
        loadAggregations
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [shipments, hasActiveSubscription, user?.id]);

  const aggregationIndex = useMemo(() => {
    const childToParent = {};
    const parentChildCount = {};
    activeAggregations.forEach((row) => {
      childToParent[row.child_shipment_id] = row.parent_shipment_id;
      parentChildCount[row.parent_shipment_id] = (parentChildCount[row.parent_shipment_id] || 0) + 1;
    });
    return { childToParent, parentChildCount };
  }, [activeAggregations]);

  const shipmentById = useMemo(() => {
    const map = {};
    (shipments || []).forEach((s) => { map[s.id] = s; });
    return map;
  }, [shipments]);

  const getAggregationRole = (shipmentId) => {
    const childCount = aggregationIndex.parentChildCount[shipmentId];
    if (childCount) {
      return { role: 'parent', childCount };
    }
    const parentId = aggregationIndex.childToParent[shipmentId];
    if (parentId) {
      return { role: 'child', parentId };
    }
    return null;
  };

  const formatShortEpc = (epcUri) => {
    if (!epcUri) return '—';
    return epcUri.replace(/^urn:epc:id:sscc:/, '') || epcUri.split(':').pop() || epcUri;
  };

  const sensorItems = (sensors || []).map((s) => ({ id: s.id, label: s.display_name || s.name || s.id }));
  const zoneItems = (zones || []).map((z) => ({ id: z.id, label: z.name }));

  const handleDeleteClick = (shipment) => {
    setShipmentToDelete(shipment);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!shipmentToDelete) return;
    setIsDeleting(true);
    try {
      await deleteShipment(shipmentToDelete.id);
      setNotification({ kind: 'success', title: 'Shipment deleted', subtitle: shipmentToDelete.epc_uri });
      setDeleteModalOpen(false);
      setShipmentToDelete(null);
      refresh();
    } catch (err) {
      setNotification({ kind: 'error', title: 'Delete failed', subtitle: err.message });
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper to pull an address block from either `metadata.x` or a top-level `shipment.x`
  // and normalise any camelCase `postalCode` key to snake_case `postal_code`.
  const getAddressBlock = (source, key) => {
    const block = source?.metadata?.[key] || source?.[key] || {};
    return {
      name: block.name || '',
      company: block.company || '',
      address: block.address || '',
      city: block.city || '',
      state: block.state || '',
      postal_code: block.postal_code ?? block.postalCode ?? '',
      country: block.country || '',
    };
  };

  const verifyRecipientEmail = async (email, setRegistered, setLoading) => {
    const trimmed = (email || '').trim();
    if (!trimmed || !trimmed.includes('@')) {
      setRegistered(null);
      return null;
    }
    setLoading(true);
    try {
      const registered = await checkRecipientRegistered(trimmed);
      setRegistered(registered);
      return registered;
    } catch (err) {
      console.warn('Recipient registration check failed:', err);
      setRegistered(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async (shipment) => {
    const meta = shipment.metadata || {};
    const sender = getAddressBlock(shipment, 'sender');
    const receiver = getAddressBlock(shipment, 'receiver');
    const carrier = meta.carrier || shipment.carrier || {};
    setEditShipment(shipment);
    setEditEpcUri(shipment.epc_uri || '');
    setEditSensorId(shipment.sensor_id || null);
    setEditZoneId(shipment.zone_id || null);
    setEditStatus(shipment.status || 'commissioned');
    setEditProductName(meta.product_name || '');
    setEditLotNumber(meta.lot_number || '');
    setEditGtin(meta.gtin || '');
    setEditSscc(meta.sscc || '');
    setEditQuantity(meta.quantity != null ? String(meta.quantity) : '');
    setEditQuantityUnit(meta.quantity_unit || 'units');
    setEditExpiryDate(meta.expiry_date || '');
    setEditGtinCorrected(false);
    setEditSsccCorrected(false);
    setEditStep(0);
    const hasGs1Data = !!(
      meta.sscc || meta.gtin || meta.lot_number || meta.expiry_date || meta.quantity != null
    );
    setEditGs1InputMode(hasGs1Data ? 'manual' : 'autogenerate');
    setEditSenderName(sender.name);
    setEditSenderCompany(sender.company);
    setEditSenderAddress(sender.address);
    setEditSenderCity(sender.city);
    setEditSenderState(sender.state);
    setEditSenderPostalCode(sender.postal_code);
    setEditSenderCountry(sender.country);
    setEditReceiverName(receiver.name);
    setEditReceiverCompany(receiver.company);
    setEditReceiverAddress(receiver.address);
    setEditReceiverCity(receiver.city);
    setEditReceiverState(receiver.state);
    setEditReceiverPostalCode(receiver.postal_code);
    setEditReceiverCountry(receiver.country);
    setEditCarrierName(carrier.name || '');
    setEditCarrierTrackingNumber(carrier.tracking_number || '');
    setEditRecipientEmail('');
    setEditRecipientRole('carrier');
    setEditRecipientRegistered(null);
    setEditModalOpen(true);

    try {
      const pending = await getPendingAuthorization(shipment.id);
      if (pending?.authorized_email) {
        setEditRecipientEmail(pending.authorized_email);
        setEditRecipientRole(pending.authorized_role || 'carrier');
        verifyRecipientEmail(
          pending.authorized_email,
          setEditRecipientRegistered,
          setEditRecipientCheckLoading
        );
      }
    } catch (err) {
      console.warn('Could not load pending authorization:', err);
    }
  };

  const handleSaveMetadata = async () => {
    if (!editShipment) return;
    const trimmedRecipient = editRecipientEmail.trim();
    if (!trimmedRecipient || !trimmedRecipient.includes('@')) {
      setNotification({
        kind: 'error',
        title: 'Recipient email required',
        subtitle: 'Enter the email of the person authorized to receive this shipment.',
      });
      setEditStep(2);
      return;
    }

    setIsMetaSaving(true);
    try {
      const gs1 = resolveEditGs1FieldsForSubmit();
      const metadata = {};
      if (editProductName.trim()) metadata.product_name = editProductName.trim();
      if (gs1.lotNumber) metadata.lot_number = gs1.lotNumber;
      if (gs1.gtin) {
        metadata.gtin = normalizeGtin14(gs1.gtin) || gs1.gtin;
      }
      if (gs1.sscc) {
        metadata.sscc = normalizeSscc18(gs1.sscc) || gs1.sscc;
      }
      if (gs1.quantity != null) metadata.quantity = gs1.quantity;
      if (editQuantityUnit) metadata.quantity_unit = editQuantityUnit;
      if (gs1.expiryDate) metadata.expiry_date = gs1.expiryDate;

      const senderData = {};
      if (editSenderName.trim()) senderData.name = editSenderName.trim();
      if (editSenderCompany.trim()) senderData.company = editSenderCompany.trim();
      if (editSenderAddress.trim()) senderData.address = editSenderAddress.trim();
      if (editSenderCity.trim()) senderData.city = editSenderCity.trim();
      if (editSenderState.trim()) senderData.state = editSenderState.trim();
      if (editSenderPostalCode.trim()) senderData.postal_code = editSenderPostalCode.trim();
      if (editSenderCountry.trim()) senderData.country = editSenderCountry.trim();
      if (Object.keys(senderData).length > 0) metadata.sender = senderData;

      const receiverData = {};
      if (editReceiverName.trim()) receiverData.name = editReceiverName.trim();
      if (editReceiverCompany.trim()) receiverData.company = editReceiverCompany.trim();
      if (editReceiverAddress.trim()) receiverData.address = editReceiverAddress.trim();
      if (editReceiverCity.trim()) receiverData.city = editReceiverCity.trim();
      if (editReceiverState.trim()) receiverData.state = editReceiverState.trim();
      if (editReceiverPostalCode.trim()) receiverData.postal_code = editReceiverPostalCode.trim();
      if (editReceiverCountry.trim()) receiverData.country = editReceiverCountry.trim();
      if (Object.keys(receiverData).length > 0) metadata.receiver = receiverData;

      const carrierData = {};
      if (editCarrierName.trim()) carrierData.name = editCarrierName.trim();
      const editTrackingNum = editCarrierTrackingNumber.trim() || generateTrackingNumber();
      if (editTrackingNum) carrierData.tracking_number = editTrackingNum;
      if (Object.keys(carrierData).length > 0) metadata.carrier = carrierData;

      await updateShipment(editShipment.id, {
        epcUri: gs1.epcUri || editEpcUri.trim() || undefined,
        sensorId: editSensorId || undefined,
        zoneId: editZoneId || undefined,
        status: editStatus,
        metadata: Object.keys(metadata).length > 0 ? metadata : null,
      });

      const { isRegistered } = await designateRecipient(
        editShipment.id,
        trimmedRecipient,
        editRecipientRole || 'receiver',
        { ...editShipment, metadata }
      );

      setNotification({
        kind: isRegistered ? 'success' : 'warning',
        title: isRegistered ? 'Shipment updated' : 'Shipment updated — recipient not registered',
        subtitle: isRegistered
          ? 'Details saved. Recipient notified by email.'
          : 'User is not registered. They were sent a link to register for Chillverify before they can receive this shipment.',
      });
      setEditModalOpen(false);
      setEditShipment(null);
      refresh();
    } catch (err) {
      setNotification({ kind: 'error', title: 'Save failed', subtitle: err.message });
    } finally {
      setIsMetaSaving(false);
    }
  };

  const handleReceiveScanSuccess = async (decodedText) => {
    setReceiveScannerOpen(false);
    setReceiveLookingUp(true);
    try {
      const found = await getShipmentByEpc(decodedText.trim());
      if (!found) {
        setNotification({ kind: 'error', title: 'Not found', subtitle: `No shipment found for EPC: ${decodedText}` });
        return;
      }

      // Secure handover check: only the person designated by the current
      // custodian may receive this shipment. This is a UI-level early warning —
      // the actual handoff confirmation is enforced server-side regardless.
      const { authorized, pendingAuthorization, currentUserEmail } = await checkHandoffAuthorization(found.id);
      if (!authorized) {
        setNotification({
          kind: 'error',
          title: 'Not authorized',
          subtitle: pendingAuthorization
            ? `This shipment is authorized for a different recipient. You're signed in as ${currentUserEmail || 'an unrecognized user'} — sign in with the designated recipient's email to receive it.`
            : 'No authorized recipient has been designated for this shipment yet, or you do not have permission to view it.',
        });
        return;
      }

      setNotification({ kind: 'success', title: 'Shipment found', subtitle: found.epc_uri });
      navigate(`/dashboard/shipments/${found.id}`);
    } catch (err) {
      setNotification({ kind: 'error', title: 'Lookup failed', subtitle: err.message });
    } finally {
      setReceiveLookingUp(false);
    }
  };

  const resetCreateForm = () => {
    setCreateStep(0);
    setSelectedSensorId(null);
    setSelectedZoneId(null);
    setEpcInput('');
    setInitialState('commissioned');
    setProductName('');
    setLotNumber('');
    setGtin('');
    setSscc('');
    setQuantity('');
    setQuantityUnit('units');
    setExpiryDate('');
    setRecipientEmail('');
    setRecipientRole('carrier');
    setRecipientRegistered(null);
    setRecipientCheckLoading(false);
    setCreateGtinCorrected(false);
    setCreateSsccCorrected(false);
    setGs1InputMode('autogenerate');
    setSenderName('');
    setSenderCompany('');
    setSenderAddress('');
    setSenderCity('');
    setSenderState('');
    setSenderPostalCode('');
    setSenderCountry('');
    setReceiverName('');
    setReceiverCompany('');
    setReceiverAddress('');
    setReceiverCity('');
    setReceiverState('');
    setReceiverPostalCode('');
    setReceiverCountry('');
    setCarrierName('');
    setCarrierTrackingNumber('');
  };

  const handleCreateGtinChange = (value) => {
    const { value: nextValue, corrected } = applyGtinInputNormalization(value);
    setGtin(nextValue);
    setCreateGtinCorrected(corrected);
  };

  const handleCreateSsccChange = (value) => {
    const { value: nextValue, corrected } = applySsccInputNormalization(value);
    setSscc(nextValue);
    setCreateSsccCorrected(corrected);
  };

  const handleEditGtinChange = (value) => {
    const { value: nextValue, corrected } = applyGtinInputNormalization(value);
    setEditGtin(nextValue);
    setEditGtinCorrected(corrected);
  };

  const handleEditSsccChange = (value) => {
    const { value: nextValue, corrected } = applySsccInputNormalization(value);
    setEditSscc(nextValue);
    setEditSsccCorrected(corrected);
  };

  const applyAutogeneratedGs1Fields = (preserveGtin = false) => {
    const fields = generateGs1ShipmentFields({
      gtin: preserveGtin && gtin ? gtin : undefined,
      lotNumeric: true,
    });
    setSscc(fields.sscc);
    setGtin(fields.gtin);
    setLotNumber(fields.lotNumber);
    setExpiryDate(fields.expiryDate);
    setQuantity(String(fields.quantity));
    setEpcInput(fields.epcUri);
    setCreateGtinCorrected(false);
    setCreateSsccCorrected(false);
    return fields;
  };

  const gs1FieldsNeedAutofill = () => (
    !sscc.trim()
    || !gtin.trim()
    || !lotNumber.trim()
    || !expiryDate.trim()
    || quantity === '' || quantity == null
  );

  const ensureAutogeneratedGs1IfNeeded = () => {
    if (gs1InputMode === 'autogenerate' && gs1FieldsNeedAutofill()) {
      applyAutogeneratedGs1Fields(!!gtin.trim());
    }
  };

  /** Resolve GS1 values at submit — fills synchronously if autogenerate left fields empty. */
  const resolveGs1FieldsForSubmit = () => {
    if (gs1InputMode === 'autogenerate' && gs1FieldsNeedAutofill()) {
      return generateGs1ShipmentFields({
        gtin: gtin.trim() || undefined,
        lotNumeric: true,
      });
    }
    return {
      sscc: sscc.trim(),
      gtin: gtin.trim(),
      lotNumber: lotNumber.trim(),
      expiryDate: expiryDate.trim(),
      quantity: quantity !== '' && quantity != null ? Number(quantity) : undefined,
      epcUri: epcInput.trim(),
    };
  };

  const handleGs1InputModeChange = (mode) => {
    setGs1InputMode(mode);
    if (mode === 'autogenerate') {
      applyAutogeneratedGs1Fields(!!gtin);
    }
  };

  const gs1FieldsReadOnly = gs1InputMode === 'autogenerate';

  const applyAutogeneratedEditGs1Fields = (preserveGtin = false) => {
    const fields = generateGs1ShipmentFields({
      gtin: preserveGtin && editGtin ? editGtin : undefined,
      lotNumeric: true,
    });
    setEditSscc(fields.sscc);
    setEditGtin(fields.gtin);
    setEditLotNumber(fields.lotNumber);
    setEditExpiryDate(fields.expiryDate);
    setEditQuantity(String(fields.quantity));
    setEditEpcUri(fields.epcUri);
    setEditGtinCorrected(false);
    setEditSsccCorrected(false);
    return fields;
  };

  const editGs1FieldsNeedAutofill = () => (
    !editSscc.trim()
    || !editGtin.trim()
    || !editLotNumber.trim()
    || !editExpiryDate.trim()
    || editQuantity === '' || editQuantity == null
  );

  const ensureAutogeneratedEditGs1IfNeeded = () => {
    if (editGs1InputMode === 'autogenerate' && editGs1FieldsNeedAutofill()) {
      applyAutogeneratedEditGs1Fields(!!editGtin.trim());
    }
  };

  const resolveEditGs1FieldsForSubmit = () => {
    if (editGs1InputMode === 'autogenerate' && editGs1FieldsNeedAutofill()) {
      return generateGs1ShipmentFields({
        gtin: editGtin.trim() || undefined,
        lotNumeric: true,
      });
    }
    return {
      sscc: editSscc.trim(),
      gtin: editGtin.trim(),
      lotNumber: editLotNumber.trim(),
      expiryDate: editExpiryDate.trim(),
      quantity: editQuantity !== '' && editQuantity != null ? Number(editQuantity) : undefined,
      epcUri: editEpcUri.trim(),
    };
  };

  const handleEditGs1InputModeChange = (mode) => {
    setEditGs1InputMode(mode);
    if (mode === 'autogenerate') {
      applyAutogeneratedEditGs1Fields(!!editGtin.trim());
    }
  };

  const editGs1FieldsReadOnly = editGs1InputMode === 'autogenerate';

  const handleScanSuccess = (decodedText) => {
    setEpcInput(decodedText);
    setScannerOpen(false);
    setNotification({
      kind: 'success',
      title: 'Barcode scanned',
      subtitle: decodedText,
    });
  };

  const handleCreateShipment = async () => {
    if (!selectedSensorId || !selectedZoneId) {
      setNotification({ kind: 'error', title: 'Missing fields', subtitle: 'Please select a sensor and origin zone.' });
      setCreateStep(0);
      return;
    }

    const trimmedRecipient = recipientEmail.trim();
    if (!trimmedRecipient || !trimmedRecipient.includes('@')) {
      setNotification({
        kind: 'error',
        title: 'Recipient email required',
        subtitle: 'Enter the email of the person authorized to receive this shipment.',
      });
      setCreateStep(2);
      return;
    }

    setIsCreating(true);
    try {
      const registered = await verifyRecipientEmail(
        trimmedRecipient,
        setRecipientRegistered,
        setRecipientCheckLoading
      );

      const gs1 = resolveGs1FieldsForSubmit();

      const shipment = await createShipment({
        sensorId: selectedSensorId,
        zoneId: selectedZoneId,
        epcUri: gs1.epcUri || epcInput,
        initialStatus: initialState,
        productName: productName.trim() || undefined,
        lotNumber: gs1.lotNumber || undefined,
        gtin: gs1.gtin || undefined,
        sscc: gs1.sscc || undefined,
        quantity: gs1.quantity != null ? gs1.quantity : undefined,
        quantityUnit: quantityUnit || undefined,
        expiryDate: gs1.expiryDate || undefined,
        authorizedRecipientEmail: trimmedRecipient,
        authorizedRecipientRole: recipientRole || undefined,
        sender: {
          name: senderName.trim() || undefined,
          company: senderCompany.trim() || undefined,
          address: senderAddress.trim() || undefined,
          city: senderCity.trim() || undefined,
          state: senderState.trim() || undefined,
          postalCode: senderPostalCode.trim() || undefined,
          country: senderCountry.trim() || undefined,
        },
        receiver: {
          name: receiverName.trim() || undefined,
          company: receiverCompany.trim() || undefined,
          address: receiverAddress.trim() || undefined,
          city: receiverCity.trim() || undefined,
          state: receiverState.trim() || undefined,
          postalCode: receiverPostalCode.trim() || undefined,
          country: receiverCountry.trim() || undefined,
        },
        carrier: {
          name: carrierName.trim() || undefined,
          trackingNumber: carrierTrackingNumber.trim() || generateTrackingNumber(),
        },
      });

      const isRegistered = shipment.recipientIsRegistered ?? registered;
      setNotification({
        kind: isRegistered === false ? 'warning' : 'success',
        title: isRegistered === false
          ? 'Shipment started — recipient not registered'
          : 'Shipment started',
        subtitle: isRegistered === false
          ? 'User is not registered. They were sent a link to register for Chillverify before they can receive this shipment.'
          : shipment.epc_uri,
      });
      setCreateModalOpen(false);
      resetCreateForm();
      refresh();
      navigate(`/dashboard/shipments/${shipment.id}`);
    } catch (err) {
      console.error('Failed to create shipment:', err);
      setNotification({ kind: 'error', title: 'Failed to start shipment', subtitle: err.message || 'Please try again.' });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <SubscriptionGate
      feature="shipment traceability"
      disabled={!hasActiveSubscription}
      showChildrenGreyed
    >
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="shipments-list-page">
        <Modal
          open={createModalOpen}
          modalHeading="Start New Tracked Shipment"
          primaryButtonText={
            isCreating ? 'Starting…' :
            createStep === 2 ? 'Bind & Initialize EPCIS Trip' : 'Next'
          }
          primaryButtonDisabled={isCreating}
          secondaryButtonText={
            createStep === 0 ? 'Cancel' : 'Back'
          }
          onRequestClose={() => { setCreateModalOpen(false); resetCreateForm(); }}
          onRequestSubmit={() => {
            if (createStep < 2) {
              const nextStep = createStep + 1;
              if (gs1InputMode === 'autogenerate') {
                if (nextStep === 1) {
                  applyAutogeneratedGs1Fields(false);
                } else if (nextStep === 2) {
                  ensureAutogeneratedGs1IfNeeded();
                }
              }
              setCreateStep(nextStep);
            } else {
              handleCreateShipment();
            }
          }}
          onSecondarySubmit={() => {
            if (createStep === 0) {
              setCreateModalOpen(false);
              resetCreateForm();
            } else {
              setCreateStep(createStep - 1);
            }
          }}
          size="lg"
        >
          <ProgressIndicator currentIndex={createStep} spaceEqually style={{ marginBottom: '24px' }}>
            <ProgressStep label="Setup" />
            <ProgressStep label="GS1 Barcode Data" />
            <ProgressStep label="Sender / Receiver / Carrier" />
          </ProgressIndicator>

          {/* Step 0: Shipment Setup — EPC, sensor, zone, initial state */}
          {createStep === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      id="shipment-epc"
                      labelText={<FieldInfo label="EPC / SSCC Code" info="Scan an existing GS1 barcode on your packaging (SSCC pallet label or GTIN product label), or type the EPC URI manually. In autogenerate mode this is set from SSCC on the GS1 step. If left blank in manual mode, Chillverify will auto-generate a unique urn:chillverify:shipment: identifier." example="urn:epc:id:sscc:0614141.0000000001" />}
                      placeholder={gs1InputMode === 'autogenerate' ? 'Set from SSCC on GS1 step' : 'Auto-generated if left blank'}
                      value={epcInput}
                      onChange={(e) => setEpcInput(e.target.value)}
                      readOnly={gs1InputMode === 'autogenerate'}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0, paddingBottom: '1.5rem' }}>
                    <Button
                      kind="secondary"
                      size="field"
                      renderIcon={Camera}
                      iconDescription="Scan barcode"
                      hasIconOnly
                      onClick={() => setScannerOpen(true)}
                      data-testid="scan-epc-btn"
                    />
                  </div>
                </div>
              </div>
              <Dropdown
                id="shipment-sensor"
                titleText="Sensor"
                label="Select sensor to bind"
                items={sensorItems}
                itemToString={(item) => (item ? item.label : '')}
                onChange={({ selectedItem }) => setSelectedSensorId(selectedItem?.id || null)}
              />
              <Dropdown
                id="shipment-zone"
                titleText="Origin Zone"
                label="Select origin zone"
                items={zoneItems}
                itemToString={(item) => (item ? item.label : '')}
                onChange={({ selectedItem }) => setSelectedZoneId(selectedItem?.id || null)}
              />
              <RadioButtonGroup
                legendText={<FieldInfo label="Lifecycle Initial State" info="Commissioned: product is being registered and prepared for shipment. In Transit: shipment has already departed the origin zone." />}
                name="initial-state"
                valueSelected={initialState}
                onChange={(value) => setInitialState(value)}
              >
                <RadioButton labelText="Commissioned" value="commissioned" id="state-commissioned" />
                <RadioButton labelText="In Transit" value="in_transit" id="state-in-transit" />
              </RadioButtonGroup>
            </div>
          )}

          {/* Step 1: GS1 Barcode Data — AI-segregated fields */}
          {createStep === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px',
                flexWrap: 'wrap',
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <RadioButtonGroup
                  legendText="GS1 barcode identifiers"
                  name="gs1-input-mode"
                  valueSelected={gs1InputMode}
                  onChange={(value) => handleGs1InputModeChange(value)}
                >
                  <RadioButton
                    labelText="Autogenerate (SSCC, lot, GTIN, expiry, count)"
                    value="autogenerate"
                    id="gs1-mode-auto"
                  />
                  <RadioButton
                    labelText="Enter manually (scan or type)"
                    value="manual"
                    id="gs1-mode-manual"
                  />
                </RadioButtonGroup>
                {gs1InputMode === 'autogenerate' && (
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Renew}
                    onClick={() => applyAutogeneratedGs1Fields(!!gtin)}
                    data-testid="regenerate-gs1-btn"
                  >
                    Regenerate
                  </Button>
                )}
              </div>

              <TextInput
                id="shipment-product-name"
                labelText={<FieldInfo label="Product Name" info="Human-readable description printed on the logistics label. Not encoded in any barcode symbol." example="Fresh Salmon Fillets" />}
                placeholder="Fresh Salmon Fillets"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
              />

              {/* Logistic Unit — GS1-128 barcode #1 */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Logistic Unit — GS1-128 Barcode #1
                </p>
                <TextInput
                  id="shipment-sscc"
                  labelText={<FieldInfo label="AI (00) — SSCC" info="18-digit Serial Shipping Container Code. Encoded alone in its own GS1-128 symbol on the bottom panel of the logistics label. Checksum is auto-corrected if invalid." example="006141410000000001" />}
                  placeholder="006141410000000001"
                  value={sscc}
                  onChange={(e) => handleCreateSsccChange(e.target.value)}
                  readOnly={gs1FieldsReadOnly}
                />
                {createSsccCorrected && (
                  <InlineNotification
                    kind="info"
                    title="SSCC checksum auto-corrected"
                    subtitle="Invalid checksum detected and fixed automatically."
                    lowContrast
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>

              {/* Product Content — GS1-128 barcode #2 + DataMatrix */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Product Content — GS1-128 Barcode #2 & DataMatrix
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <TextInput
                    id="shipment-gtin"
                    labelText={<FieldInfo label="AI (01) — GTIN" info="8–14 digit Global Trade Item Number including check digit. Fixed-length AI encoded in GS1-128 and DataMatrix. Checksum auto-corrected if invalid." example="00614141000005" />}
                    placeholder="00614141000005"
                    value={gtin}
                    onChange={(e) => handleCreateGtinChange(e.target.value)}
                    readOnly={gs1FieldsReadOnly}
                  />
                  {createGtinCorrected && (
                    <InlineNotification
                      kind="info"
                      title="GTIN checksum auto-corrected"
                      subtitle="Invalid checksum detected and fixed automatically."
                      lowContrast
                      style={{ marginTop: '8px' }}
                    />
                  )}
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="shipment-lot"
                        labelText={<FieldInfo label="AI (10) — Batch / Lot" info="Variable-length AI (max 20 characters). Identifies the production batch or lot for traceability. Autogenerate produces a 7-digit lot number." example="1234567" />}
                        placeholder="1234567"
                        value={lotNumber}
                        onChange={(e) => setLotNumber(e.target.value)}
                        readOnly={gs1FieldsReadOnly}
                      />
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                      <DatePicker
                        datePickerType="single"
                        dateFormat="m/d/Y"
                        value={isoDateToCarbonValue(expiryDate)}
                        onChange={(dates) => setExpiryDate(carbonDatesToIso(dates))}
                        readOnly={gs1FieldsReadOnly}
                      >
                        <DatePickerInput
                          id="shipment-expiry"
                          labelText={<FieldInfo label="AI (17) — Expiry Date" info="Fixed-length AI encoded as YYMMDD. Autogenerate defaults to 90 days from today." />}
                          placeholder="mm/dd/yyyy"
                          size="md"
                          disabled={gs1FieldsReadOnly}
                        />
                      </DatePicker>
                    </div>
                  </div>
                </div>
              </div>

              {/* Count — GS1-128 barcode #3 */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Count — GS1-128 Barcode #3
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 120px' }}>
                    <NumberInput
                      id="shipment-quantity"
                      label={<FieldInfo label="AI (37) — Count" info="Count of trade items in the logistic unit. Encoded in its own separate GS1-128 symbol because AIs (01) and (37) are mutually exclusive within the same symbol per GS1 General Specifications." />}
                      value={quantity}
                      onChange={(e, { value }) => setQuantity(value)}
                      min={0}
                      step={1}
                      disabled={gs1FieldsReadOnly}
                    />
                  </div>
                  <div style={{ flex: '1 1 150px' }}>
                    <Select
                      id="shipment-quantity-unit"
                      labelText="Unit"
                      value={quantityUnit}
                      onChange={(e) => setQuantityUnit(e.target.value)}
                      disabled={gs1FieldsReadOnly}
                    >
                      <SelectItem value="units" text="units" />
                      <SelectItem value="cases" text="cases" />
                      <SelectItem value="pallets" text="pallets" />
                      <SelectItem value="kg" text="kg" />
                      <SelectItem value="boxes" text="boxes" />
                      <SelectItem value="crates" text="crates" />
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Sender, Receiver & Carrier Details */}
          {createStep === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Sender (Ship-From) */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Sender (Ship-From)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="sender-name"
                        labelText="Contact Name"
                        placeholder="e.g. John Smith"
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="sender-company"
                        labelText="Company Name"
                        placeholder="e.g. FreshFarms Co."
                        value={senderCompany}
                        onChange={(e) => setSenderCompany(e.target.value)}
                      />
                    </div>
                  </div>
                  <TextInput
                    id="sender-address"
                    labelText="Street Address"
                    placeholder="e.g. 123 Harvest Lane"
                    value={senderAddress}
                    onChange={(e) => setSenderAddress(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 150px' }}>
                      <TextInput
                        id="sender-city"
                        labelText="City"
                        value={senderCity}
                        onChange={(e) => setSenderCity(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 100px' }}>
                      <TextInput
                        id="sender-state"
                        labelText="State / Province"
                        value={senderState}
                        onChange={(e) => setSenderState(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <TextInput
                        id="sender-postal"
                        labelText="Postal Code"
                        value={senderPostalCode}
                        onChange={(e) => setSenderPostalCode(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <TextInput
                        id="sender-country"
                        labelText="Country"
                        placeholder="e.g. Australia"
                        value={senderCountry}
                        onChange={(e) => setSenderCountry(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Receiver (Ship-To) */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Receiver (Ship-To)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="receiver-name"
                        labelText="Contact Name"
                        placeholder="e.g. Jane Doe"
                        value={receiverName}
                        onChange={(e) => setReceiverName(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="receiver-company"
                        labelText="Company Name"
                        placeholder="e.g. ColdStorage Pty Ltd"
                        value={receiverCompany}
                        onChange={(e) => setReceiverCompany(e.target.value)}
                      />
                    </div>
                  </div>
                  <TextInput
                    id="receiver-address"
                    labelText="Street Address"
                    placeholder="e.g. 456 Industrial Way"
                    value={receiverAddress}
                    onChange={(e) => setReceiverAddress(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 150px' }}>
                      <TextInput
                        id="receiver-city"
                        labelText="City"
                        value={receiverCity}
                        onChange={(e) => setReceiverCity(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 100px' }}>
                      <TextInput
                        id="receiver-state"
                        labelText="State / Province"
                        value={receiverState}
                        onChange={(e) => setReceiverState(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <TextInput
                        id="receiver-postal"
                        labelText="Postal Code"
                        value={receiverPostalCode}
                        onChange={(e) => setReceiverPostalCode(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <TextInput
                        id="receiver-country"
                        labelText="Country"
                        placeholder="e.g. Australia"
                        value={receiverCountry}
                        onChange={(e) => setReceiverCountry(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Carrier Details */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Carrier
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <TextInput
                      id="carrier-name"
                      labelText={<FieldInfo label="Carrier Name" info="Name of the carrier or logistics company transporting this shipment." example="FastFreight Logistics" />}
                      placeholder="FastFreight Logistics"
                      value={carrierName}
                      onChange={(e) => setCarrierName(e.target.value)}
                    />
                  </div>
                  <div style={{ flex: '1 1 200px' }}>
                    <TextInput
                      id="carrier-tracking"
                      labelText={<FieldInfo label="Tracking Number" info="Carrier tracking reference. Auto-generated if left blank when the label is created in-app." example="CV-20240728-A1B2C3D4" />}
                      placeholder="Auto-generated if left blank"
                      value={carrierTrackingNumber}
                      onChange={(e) => setCarrierTrackingNumber(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Authorized Recipient — required */}
              <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '16px', marginTop: '8px' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Authorized Recipient (required)
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <TextInput
                      id="shipment-recipient-email"
                      labelText={<FieldInfo label="Recipient Email *" info="Only this person, logged in with this exact email, can receive or hand off this shipment. Required." example="driver@carrierco.com" />}
                      placeholder="driver@carrierco.com"
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => {
                        setRecipientEmail(e.target.value);
                        setRecipientRegistered(null);
                      }}
                      onBlur={() => verifyRecipientEmail(
                        recipientEmail,
                        setRecipientRegistered,
                        setRecipientCheckLoading
                      )}
                      invalid={recipientEmail.length > 0 && !recipientEmail.includes('@')}
                      invalidText="Enter a valid email address"
                    />
                  </div>
                  <div style={{ flex: '1 1 180px' }}>
                    <Select
                      id="shipment-recipient-role"
                      labelText="Recipient Role"
                      value={recipientRole}
                      onChange={(e) => setRecipientRole(e.target.value)}
                    >
                      <SelectItem value="carrier" text="Carrier / Driver" />
                      <SelectItem value="warehouse" text="Warehouse / DC" />
                      <SelectItem value="receiver" text="Receiver" />
                      <SelectItem value="producer" text="Producer" />
                    </Select>
                  </div>
                </div>
                {recipientCheckLoading && (
                  <InlineNotification
                    kind="info"
                    title="Checking registration…"
                    subtitle="Looking up this email in Chillverify"
                    lowContrast
                    hideCloseButton
                    style={{ marginTop: '12px' }}
                  />
                )}
                {recipientRegistered === false && !recipientCheckLoading && (
                  <InlineNotification
                    kind="warning"
                    title="User not registered"
                    subtitle="They will be sent a link to register for Chillverify before they can receive this shipment."
                    lowContrast
                    hideCloseButton
                    style={{ marginTop: '12px' }}
                  />
                )}
                {recipientRegistered === true && !recipientCheckLoading && (
                  <InlineNotification
                    kind="success"
                    title="Registered Chillverify user"
                    subtitle="They will be notified by email and can sign in to receive this shipment."
                    lowContrast
                    hideCloseButton
                    style={{ marginTop: '12px' }}
                  />
                )}
              </div>
            </div>
          )}

        </Modal>

        <QRCodeScanner
          isOpen={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onScanSuccess={handleScanSuccess}
        />

        <QRCodeScanner
          isOpen={receiveScannerOpen}
          onClose={() => setReceiveScannerOpen(false)}
          onScanSuccess={handleReceiveScanSuccess}
        />

        {receiveLookingUp && (
          <div style={{ position: 'fixed', top: '80px', right: '16px', zIndex: 9999, maxWidth: '400px' }}>
            <InlineNotification
              kind="info"
              title="Looking up shipment..."
              subtitle="Searching for shipment by scanned EPC"
              lowContrast
            />
          </div>
        )}

        {notification && (
          <div style={{ position: 'fixed', top: '80px', right: '16px', zIndex: 9999, maxWidth: '400px' }}>
            <InlineNotification
              kind={notification.kind}
              title={notification.title}
              subtitle={notification.subtitle}
              onCloseButtonClick={() => setNotification(null)}
              lowContrast
            />
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          open={deleteModalOpen}
          danger
          modalHeading="Delete shipment"
          modalLabel="Confirm deletion"
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
          onRequestClose={() => { setDeleteModalOpen(false); setShipmentToDelete(null); }}
          onRequestSubmit={handleConfirmDelete}
          primaryButtonDisabled={isDeleting}
          size="sm"
        >
          <p style={{ marginBottom: '16px' }}>
            Are you sure you want to delete this shipment?
          </p>
          <p style={{ color: textSecondary, fontSize: '0.875rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {shipmentToDelete?.epc_uri}
          </p>
          <p style={{ color: '#ff832b', fontSize: '0.875rem', marginTop: '12px' }}>
            This will also delete all handoff records. This action cannot be undone.
          </p>
        </Modal>

        {/* Edit Shipment Modal — mirrors the create modal as 3-step wizard */}
        <Modal
          key={editShipment?.id || 'new-edit'}
          open={editModalOpen}
          modalHeading="Edit Shipment"
          primaryButtonText={
            isMetaSaving ? 'Saving...' :
            editStep === 2 ? 'Save Changes' : 'Next'
          }
          primaryButtonDisabled={isMetaSaving}
          secondaryButtonText={
            editStep === 0 ? 'Cancel' : 'Back'
          }
          onRequestClose={() => { setEditModalOpen(false); setEditShipment(null); setEditGs1InputMode('manual'); }}
          onRequestSubmit={() => {
            if (editStep < 2) {
              const nextStep = editStep + 1;
              if (editGs1InputMode === 'autogenerate') {
                if (nextStep === 1) {
                  applyAutogeneratedEditGs1Fields(false);
                } else if (nextStep === 2) {
                  ensureAutogeneratedEditGs1IfNeeded();
                }
              }
              setEditStep(nextStep);
            } else {
              handleSaveMetadata();
            }
          }}
          onSecondarySubmit={() => {
            if (editStep === 0) {
              setEditModalOpen(false);
              setEditShipment(null);
              setEditGs1InputMode('manual');
            } else {
              setEditStep(editStep - 1);
            }
          }}
          size="lg"
        >
          <ProgressIndicator currentIndex={editStep} spaceEqually style={{ marginBottom: '24px' }}>
            <ProgressStep label="Setup" />
            <ProgressStep label="GS1 Barcode Data" />
            <ProgressStep label="Sender / Receiver / Carrier" />
          </ProgressIndicator>

          {/* Edit Step 0: Shipment Setup — EPC, sensor, zone, status */}
          {editStep === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      id="edit-epc"
                      labelText={<FieldInfo label="EPC / SSCC Code" info="The EPC URI used in all EPCIS 2.0 events. In autogenerate mode this is set from SSCC on the GS1 step." example="urn:epc:id:sscc:0614141.0000000001" />}
                      placeholder={editGs1InputMode === 'autogenerate' ? 'Set from SSCC on GS1 step' : 'urn:epc:id:sscc:0614141.0000000001'}
                      value={editEpcUri}
                      onChange={(e) => setEditEpcUri(e.target.value)}
                      readOnly={editGs1InputMode === 'autogenerate'}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0, paddingBottom: '1.5rem' }}>
                    <Button
                      kind="secondary"
                      size="field"
                      renderIcon={Camera}
                      iconDescription="Scan barcode"
                      hasIconOnly
                      onClick={() => setEditScannerOpen(true)}
                      data-testid="edit-scan-epc-btn"
                    />
                  </div>
                </div>
              </div>
              <Dropdown
                id="edit-sensor"
                titleText="Sensor"
                label="Select sensor"
                items={sensorItems}
                itemToString={(item) => (item ? item.label : '')}
                onChange={({ selectedItem }) => setEditSensorId(selectedItem?.id || null)}
                selectedItem={sensorItems.find((s) => s.id === editSensorId) || null}
              />
              <Dropdown
                id="edit-zone"
                titleText="Zone"
                label="Select zone"
                items={zoneItems}
                itemToString={(item) => (item ? item.label : '')}
                onChange={({ selectedItem }) => setEditZoneId(selectedItem?.id || null)}
                selectedItem={zoneItems.find((z) => z.id === editZoneId) || null}
              />
              <RadioButtonGroup
                legendText={<FieldInfo label="Shipment Status" info="Commissioned: product registered and prepared. In Transit: shipment departed origin. Delivered: shipment received at destination. Quarantined: shipment held for inspection." />}
                name="edit-status"
                valueSelected={editStatus}
                onChange={(value) => setEditStatus(value)}
              >
                <RadioButton labelText="Commissioned" value="commissioned" id="edit-st-commissioned" />
                <RadioButton labelText="In Transit" value="in_transit" id="edit-st-transit" />
                <RadioButton labelText="Delivered" value="delivered" id="edit-st-delivered" />
                <RadioButton labelText="Quarantined" value="quarantined" id="edit-st-quarantined" />
              </RadioButtonGroup>
            </div>
          )}

          {/* Edit Step 1: GS1 Barcode Data — AI-segregated fields */}
          {editStep === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px',
                flexWrap: 'wrap',
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <RadioButtonGroup
                  legendText="GS1 barcode identifiers"
                  name="edit-gs1-input-mode"
                  valueSelected={editGs1InputMode}
                  onChange={(value) => handleEditGs1InputModeChange(value)}
                >
                  <RadioButton
                    labelText="Autogenerate (SSCC, lot, GTIN, expiry, count)"
                    value="autogenerate"
                    id="edit-gs1-mode-auto"
                  />
                  <RadioButton
                    labelText="Enter manually (scan or type)"
                    value="manual"
                    id="edit-gs1-mode-manual"
                  />
                </RadioButtonGroup>
                {editGs1InputMode === 'autogenerate' && (
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Renew}
                    onClick={() => applyAutogeneratedEditGs1Fields(!!editGtin.trim())}
                    data-testid="edit-regenerate-gs1-btn"
                  >
                    Regenerate
                  </Button>
                )}
              </div>

              <TextInput
                id="edit-product-name"
                labelText={<FieldInfo label="Product Name" info="Human-readable description printed on the logistics label. Not encoded in any barcode symbol." example="Fresh Salmon Fillets" />}
                placeholder="Fresh Salmon Fillets"
                value={editProductName}
                onChange={(e) => setEditProductName(e.target.value)}
              />

              {/* Logistic Unit — GS1-128 barcode #1 */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Logistic Unit — GS1-128 Barcode #1
                </p>
                <TextInput
                  id="edit-sscc"
                  labelText={<FieldInfo label="AI (00) — SSCC" info="18-digit Serial Shipping Container Code. Encoded alone in its own GS1-128 symbol on the bottom panel of the logistics label. Checksum is auto-corrected if invalid." example="006141410000000001" />}
                  placeholder="006141410000000001"
                  value={editSscc}
                  onChange={(e) => handleEditSsccChange(e.target.value)}
                  readOnly={editGs1FieldsReadOnly}
                />
                {editSsccCorrected && (
                  <InlineNotification
                    kind="info"
                    title="SSCC checksum auto-corrected"
                    subtitle="Invalid checksum detected and fixed automatically."
                    lowContrast
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>

              {/* Product Content — GS1-128 barcode #2 + DataMatrix */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Product Content — GS1-128 Barcode #2 & DataMatrix
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <TextInput
                    id="edit-gtin"
                    labelText={<FieldInfo label="AI (01) — GTIN" info="8–14 digit Global Trade Item Number including check digit. Fixed-length AI encoded in GS1-128 and DataMatrix. Checksum auto-corrected if invalid." example="00614141000005" />}
                    placeholder="00614141000005"
                    value={editGtin}
                    onChange={(e) => handleEditGtinChange(e.target.value)}
                    readOnly={editGs1FieldsReadOnly}
                  />
                  {editGtinCorrected && (
                    <InlineNotification
                      kind="info"
                      title="GTIN checksum auto-corrected"
                      subtitle="Invalid checksum detected and fixed automatically."
                      lowContrast
                      style={{ marginTop: '8px' }}
                    />
                  )}
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="edit-lot"
                        labelText={<FieldInfo label="AI (10) — Batch / Lot" info="Variable-length AI (max 20 characters). Identifies the production batch or lot for traceability. Autogenerate produces a 7-digit lot number." example="1234567" />}
                        placeholder="1234567"
                        value={editLotNumber}
                        onChange={(e) => setEditLotNumber(e.target.value)}
                        readOnly={editGs1FieldsReadOnly}
                      />
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                      <DatePicker
                        datePickerType="single"
                        dateFormat="m/d/Y"
                        value={isoDateToCarbonValue(editExpiryDate)}
                        onChange={(dates) => setEditExpiryDate(carbonDatesToIso(dates))}
                        readOnly={editGs1FieldsReadOnly}
                      >
                        <DatePickerInput
                          id="edit-expiry"
                          labelText={<FieldInfo label="AI (17) — Expiry Date" info="Fixed-length AI encoded as YYMMDD. Autogenerate defaults to 90 days from today." />}
                          placeholder="mm/dd/yyyy"
                          size="md"
                          disabled={editGs1FieldsReadOnly}
                        />
                      </DatePicker>
                    </div>
                  </div>
                </div>
              </div>

              {/* Count — GS1-128 barcode #3 */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Count — GS1-128 Barcode #3
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 120px' }}>
                    <NumberInput
                      id="edit-quantity"
                      label={<FieldInfo label="AI (37) — Count" info="Count of trade items in the logistic unit. Encoded in its own separate GS1-128 symbol because AIs (01) and (37) are mutually exclusive within the same symbol per GS1 General Specifications." />}
                      value={editQuantity}
                      onChange={(e, { value }) => setEditQuantity(value)}
                      min={0}
                      step={1}
                      disabled={editGs1FieldsReadOnly}
                    />
                  </div>
                  <div style={{ flex: '1 1 150px' }}>
                    <Select
                      id="edit-quantity-unit"
                      labelText="Unit"
                      value={editQuantityUnit}
                      onChange={(e) => setEditQuantityUnit(e.target.value)}
                      disabled={editGs1FieldsReadOnly}
                    >
                      <SelectItem value="units" text="units" />
                      <SelectItem value="cases" text="cases" />
                      <SelectItem value="pallets" text="pallets" />
                      <SelectItem value="kg" text="kg" />
                      <SelectItem value="boxes" text="boxes" />
                      <SelectItem value="crates" text="crates" />
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Step 2: Sender, Receiver & Carrier Details */}
          {editStep === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Sender (Ship-From) */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Sender (Ship-From)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="edit-sender-name"
                        labelText="Contact Name"
                        placeholder="e.g. John Smith"
                        value={editSenderName}
                        onChange={(e) => setEditSenderName(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="edit-sender-company"
                        labelText="Company Name"
                        placeholder="e.g. FreshFarms Co."
                        value={editSenderCompany}
                        onChange={(e) => setEditSenderCompany(e.target.value)}
                      />
                    </div>
                  </div>
                  <TextInput
                    id="edit-sender-address"
                    labelText="Street Address"
                    placeholder="e.g. 123 Harvest Lane"
                    value={editSenderAddress}
                    onChange={(e) => setEditSenderAddress(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 150px' }}>
                      <TextInput
                        id="edit-sender-city"
                        labelText="City"
                        value={editSenderCity}
                        onChange={(e) => setEditSenderCity(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 100px' }}>
                      <TextInput
                        id="edit-sender-state"
                        labelText="State / Province"
                        value={editSenderState}
                        onChange={(e) => setEditSenderState(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <TextInput
                        id="edit-sender-postal"
                        labelText="Postal Code"
                        value={editSenderPostalCode}
                        onChange={(e) => setEditSenderPostalCode(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <TextInput
                        id="edit-sender-country"
                        labelText="Country"
                        placeholder="e.g. Australia"
                        value={editSenderCountry}
                        onChange={(e) => setEditSenderCountry(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Receiver (Ship-To) */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Receiver (Ship-To)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="edit-receiver-name"
                        labelText="Contact Name"
                        placeholder="e.g. Jane Doe"
                        value={editReceiverName}
                        onChange={(e) => setEditReceiverName(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 200px' }}>
                      <TextInput
                        id="edit-receiver-company"
                        labelText="Company Name"
                        placeholder="e.g. ColdStorage Pty Ltd"
                        value={editReceiverCompany}
                        onChange={(e) => setEditReceiverCompany(e.target.value)}
                      />
                    </div>
                  </div>
                  <TextInput
                    id="edit-receiver-address"
                    labelText="Street Address"
                    placeholder="e.g. 456 Industrial Way"
                    value={editReceiverAddress}
                    onChange={(e) => setEditReceiverAddress(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 150px' }}>
                      <TextInput
                        id="edit-receiver-city"
                        labelText="City"
                        value={editReceiverCity}
                        onChange={(e) => setEditReceiverCity(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 100px' }}>
                      <TextInput
                        id="edit-receiver-state"
                        labelText="State / Province"
                        value={editReceiverState}
                        onChange={(e) => setEditReceiverState(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <TextInput
                        id="edit-receiver-postal"
                        labelText="Postal Code"
                        value={editReceiverPostalCode}
                        onChange={(e) => setEditReceiverPostalCode(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <TextInput
                        id="edit-receiver-country"
                        labelText="Country"
                        placeholder="e.g. Australia"
                        value={editReceiverCountry}
                        onChange={(e) => setEditReceiverCountry(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Carrier Details */}
              <div style={{
                padding: '12px 16px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
              }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Carrier
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <TextInput
                      id="edit-carrier-name"
                      labelText={<FieldInfo label="Carrier Name" info="Name of the carrier or logistics company transporting this shipment." example="FastFreight Logistics" />}
                      placeholder="FastFreight Logistics"
                      value={editCarrierName}
                      onChange={(e) => setEditCarrierName(e.target.value)}
                    />
                  </div>
                  <div style={{ flex: '1 1 200px' }}>
                    <TextInput
                      id="edit-carrier-tracking"
                      labelText={<FieldInfo label="Tracking Number" info="Carrier tracking reference. Auto-generated if left blank when the label is created in-app." example="CV-20240728-A1B2C3D4" />}
                      placeholder="Auto-generated if left blank"
                      value={editCarrierTrackingNumber}
                      onChange={(e) => setEditCarrierTrackingNumber(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Authorized Recipient — required */}
              <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '16px', marginTop: '8px' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
                  Authorized Recipient (required)
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <TextInput
                      id="edit-shipment-recipient-email"
                      labelText={<FieldInfo label="Recipient Email *" info="Only this person can receive or hand off this shipment. Required." example="driver@carrierco.com" />}
                      placeholder="driver@carrierco.com"
                      type="email"
                      value={editRecipientEmail}
                      onChange={(e) => {
                        setEditRecipientEmail(e.target.value);
                        setEditRecipientRegistered(null);
                      }}
                      onBlur={() => verifyRecipientEmail(
                        editRecipientEmail,
                        setEditRecipientRegistered,
                        setEditRecipientCheckLoading
                      )}
                    />
                  </div>
                  <div style={{ flex: '1 1 180px' }}>
                    <Select
                      id="edit-shipment-recipient-role"
                      labelText="Recipient Role"
                      value={editRecipientRole}
                      onChange={(e) => setEditRecipientRole(e.target.value)}
                    >
                      <SelectItem value="carrier" text="Carrier / Driver" />
                      <SelectItem value="warehouse" text="Warehouse / DC" />
                      <SelectItem value="receiver" text="Receiver" />
                      <SelectItem value="producer" text="Producer" />
                    </Select>
                  </div>
                </div>
                {editRecipientCheckLoading && (
                  <InlineNotification
                    kind="info"
                    title="Checking registration…"
                    subtitle="Looking up this email in Chillverify"
                    lowContrast
                    hideCloseButton
                    style={{ marginTop: '12px' }}
                  />
                )}
                {editRecipientRegistered === false && !editRecipientCheckLoading && (
                  <InlineNotification
                    kind="warning"
                    title="User not registered"
                    subtitle="They will be sent a link to register for Chillverify before they can receive this shipment."
                    lowContrast
                    hideCloseButton
                    style={{ marginTop: '12px' }}
                  />
                )}
                {editRecipientRegistered === true && !editRecipientCheckLoading && (
                  <InlineNotification
                    kind="success"
                    title="Registered Chillverify user"
                    subtitle="They will be notified by email and can sign in to receive this shipment."
                    lowContrast
                    hideCloseButton
                    style={{ marginTop: '12px' }}
                  />
                )}
              </div>
            </div>
          )}
        </Modal>

        {/* Edit scanner */}
        <QRCodeScanner
          isOpen={editScannerOpen}
          onClose={() => setEditScannerOpen(false)}
          onScanSuccess={(text) => { setEditScannerOpen(false); setEditEpcUri(text); }}
        />

        <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '24px 0' }}>
          <Grid>
            <Column lg={16} md={8} sm={4}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h1 style={{ color: textPrimary, fontSize: '1.75rem', fontWeight: 600, marginBottom: '4px' }}>Shipments</h1>
                  <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                    {filteredShipments.length} of {shipments.length} tracked shipments
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button kind="secondary" renderIcon={QrCode} onClick={() => setReceiveScannerOpen(true)} data-testid="receive-shipment-btn">
                    Receive Shipment
                  </Button>
                  <Button kind="primary" renderIcon={Add} onClick={() => setCreateModalOpen(true)} data-testid="add-shipment">
                    Start Shipment
                  </Button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: '16px' }}>
                <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
                  <Search
                    labelText="Search"
                    placeholder="Search by EPC, product, zone, or sensor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="lg"
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    kind={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={GridIcon}
                    iconDescription="Card view"
                    onClick={() => setViewMode('cards')}
                  />
                  <Button
                    kind={viewMode === 'table' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={List}
                    iconDescription="Table view"
                    onClick={() => setViewMode('table')}
                  />
                </div>
              </div>
            </Column>
          </Grid>
        </div>

        <div style={{ padding: '24px 0' }}>
          <Grid>
            {viewMode === 'cards' ? (
              /* Card View */
              filteredShipments.map((shipment) => {
                const aggRole = getAggregationRole(shipment.id);
                const parentShipment = aggRole?.role === 'child'
                  ? shipmentById[aggRole.parentId]
                  : null;

                return (
                <Column lg={8} md={4} sm={4} key={shipment.id} style={{ display: 'flex' }}>
                  <Tile
                    style={{
                      backgroundColor: bgSecondary,
                      border: `1px solid ${borderColor}`,
                      padding: '0',
                      marginBottom: '16px',
                      overflow: 'hidden',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    data-testid={`shipment-card-${shipment.id}`}
                  >
                    {/* Card Header */}
                    <div style={{ padding: '16px', borderBottom: `1px solid ${borderColor}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                          <QrCode size={16} style={{ color: textSecondary, flexShrink: 0 }} />
                          <h3 style={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                            {shipment.epc_uri?.split(':').pop() || '—'}
                          </h3>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', minHeight: '48px' }}>
                          <Tag type={STATUS_TAG_TYPE[shipment.status] || 'gray'} size="sm">
                            {STATUS_LABEL[shipment.status] || shipment.status}
                          </Tag>
                          {aggRole?.role === 'parent' && (
                            <Tag type="purple" size="sm" data-testid={`agg-parent-${shipment.id}`}>
                              Parent · {aggRole.childCount} packed
                            </Tag>
                          )}
                          {aggRole?.role === 'child' && (
                            <Tag type="teal" size="sm" data-testid={`agg-child-${shipment.id}`}>
                              Child · packed
                            </Tag>
                          )}
                        </div>
                      </div>
                      <p style={{
                        color: textSecondary,
                        fontSize: '0.75rem',
                        margin: 0,
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: '2.25em',
                      }}>
                        {shipment.epc_uri}
                      </p>
                      <p style={{
                        color: textSecondary,
                        fontSize: '0.6875rem',
                        margin: '6px 0 0 0',
                        minHeight: '1.1em',
                        lineHeight: 1.4,
                      }}>
                        {aggRole?.role === 'child' && parentShipment ? (
                          <>
                            Packed under:{' '}
                            <button
                              type="button"
                              onClick={() => navigate(`/dashboard/shipments/${parentShipment.id}`)}
                              style={{
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                color: '#0f62fe', fontFamily: 'monospace', fontSize: 'inherit',
                              }}
                            >
                              {formatShortEpc(parentShipment.epc_uri)}
                            </button>
                          </>
                        ) : aggRole?.role === 'parent' ? (
                          `Contains ${aggRole.childCount} packed child${aggRole.childCount === 1 ? '' : 'ren'}`
                        ) : (
                          '\u00a0'
                        )}
                      </p>
                    </div>

                    {/* Card Body */}
                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                      {shipment.metadata?.product_name && (
                        <div>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Product</p>
                          <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0', fontWeight: 500 }}>{shipment.metadata.product_name}</p>
                        </div>
                      )}
                      {shipment.metadata?.lot_number && (
                        <div>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Lot / Batch</p>
                          <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '2px 0 0 0', fontFamily: 'monospace' }}>{shipment.metadata.lot_number}</p>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Zone</p>
                          <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '2px 0 0 0' }}>{zoneById[shipment.zone_id]?.name || '—'}</p>
                        </div>
                        <div>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Sensor</p>
                          <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '2px 0 0 0' }}>{sensorById[shipment.sensor_id]?.display_name || sensorById[shipment.sensor_id]?.name || '—'}</p>
                        </div>
                      </div>
                      <div>
                        <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Started</p>
                        <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '2px 0 0 0' }}>
                          {shipment.started_at ? new Date(shipment.started_at).toLocaleString() : '—'}
                        </p>
                      </div>
                    </div>

                    {/* Card Footer - Actions */}
                    <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', borderTop: `1px solid ${borderColor}` }}>
                      <Button kind="ghost" size="sm" renderIcon={View} onClick={() => navigate(`/dashboard/shipments/${shipment.id}`)} data-testid={`view-shipment-${shipment.id}`}>
                        View
                      </Button>
                      <Button kind="ghost" size="sm" renderIcon={Edit} onClick={() => handleEditClick(shipment)} data-testid={`edit-shipment-${shipment.id}`}>
                        Edit
                      </Button>
                      <Button kind="danger--ghost" size="sm" renderIcon={TrashCan} onClick={() => handleDeleteClick(shipment)} data-testid={`delete-shipment-${shipment.id}`}>
                        Delete
                      </Button>
                    </div>
                  </Tile>
                </Column>
                );
              })
            ) : (
              /* Table View */
              <Column lg={16} md={8} sm={4}>
                {isLoading && (
                  <p style={{ color: textSecondary }}>Loading shipments...</p>
                )}
                {error && (
                  <p style={{ color: '#da1e28' }}>Error loading shipments: {error}</p>
                )}
                {!isLoading && !error && filteredShipments.length === 0 && (
                  <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', textAlign: 'center' }}>
                    <p style={{ color: textSecondary }}>No shipments yet. Start your first tracked shipment above.</p>
                  </Tile>
                )}
                {!isLoading && filteredShipments.length > 0 && (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                        <thead>
                          <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                            <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>EPC / SSCC</th>
                            <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Product</th>
                            <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Status</th>
                            <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Zone</th>
                            <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Sensor</th>
                            <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Started</th>
                            <th style={{ padding: '16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedShipments.map((shipment, idx) => (
                            <tr
                              key={shipment.id}
                              style={{ borderBottom: `1px solid ${borderColor}`, backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary }}
                              data-testid={`shipment-row-${shipment.id}`}
                            >
                              <td style={{ padding: '16px', color: textPrimary, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                                {shipment.epc_uri || '—'}
                              </td>
                              <td style={{ padding: '16px', color: textSecondary, fontSize: '0.8125rem' }}>
                                {shipment.metadata?.product_name || '—'}
                                {shipment.metadata?.lot_number && (
                                  <span style={{ display: 'block', fontSize: '0.6875rem', opacity: 0.8 }}>
                                    Lot: {shipment.metadata.lot_number}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '16px' }}>
                                <Tag type={STATUS_TAG_TYPE[shipment.status] || 'gray'} size="sm">
                                  {STATUS_LABEL[shipment.status] || shipment.status}
                                </Tag>
                              </td>
                              <td style={{ padding: '16px', color: textSecondary }}>
                                {zoneById[shipment.zone_id]?.name || '—'}
                              </td>
                              <td style={{ padding: '16px', color: textSecondary }}>
                                {sensorById[shipment.sensor_id]?.display_name || sensorById[shipment.sensor_id]?.name || '—'}
                              </td>
                              <td style={{ padding: '16px', color: textSecondary, fontSize: '0.8125rem' }}>
                                {shipment.started_at ? new Date(shipment.started_at).toLocaleString() : '—'}
                              </td>
                              <td style={{ padding: '16px' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                  <Button kind="ghost" size="sm" hasIconOnly renderIcon={View} iconDescription="View" onClick={() => navigate(`/dashboard/shipments/${shipment.id}`)} data-testid={`view-shipment-${shipment.id}`} />
                                  <Button kind="ghost" size="sm" hasIconOnly renderIcon={Edit} iconDescription="Edit" onClick={() => handleEditClick(shipment)} data-testid={`edit-shipment-${shipment.id}`} />
                                  <Button kind="danger--ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Delete" onClick={() => handleDeleteClick(shipment)} data-testid={`delete-shipment-${shipment.id}`} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {totalItems > 0 && (
                      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                          backwardText="Previous"
                          forwardText="Next"
                          itemsPerPageText="Items per page:"
                          page={page}
                          pageNumberText="Page Number"
                          pageSize={pageSize}
                          pageSizes={[10, 20, 30, 40, 50]}
                          size="md"
                          totalItems={totalItems}
                          onChange={({ page, pageSize }) => {
                            setPage(page);
                            setPageSize(pageSize);
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </Column>
            )}
          </Grid>
        </div>
      </div>
    </SubscriptionGate>
  );
};

export default ShipmentsListPage;
